/**
 * Admin Listing API
 * ადმინისტრატორის ლისტინგის API
 */

import { get } from '@aiia/portal-core';

export interface SubscriptionAdminListResponse {
  id: string;
  userId: string;
  userEmail: string | null;
  planCode: string | null;
  planName: string | null;
  status: string;
  billingCycle: string | null;
  currentPrice: number | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  discountPercent: number | null;
  discountEndsAt: string | null;
  canceledAt: string | null;
  createdAt: string;
}

export interface WidgetAdminListResponse {
  id: string;
  name: string;
  assistantName: string | null;
  userId: string;
  userEmail: string | null;
  status: string;
  position: string | null;
  isDefault: boolean;
  toolCount: number;
  fileCount: number;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
}

export interface ApiKeyAdminListResponse {
  id: string;
  name: string;
  keyPrefix: string;
  userId: string;
  userEmail: string | null;
  status: string;
  allowedDomains: string[];
  allowedIps: string[];
  lastUsedAt: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface DataFileAdminListResponse {
  id: string;
  fileName: string;
  fileType: string | null;
  userId: string;
  userEmail: string | null;
  fileSize: number;
  status: string;
  vectorized: boolean;
  chunkCount: number | null;
  createdAt: string;
  vectorizedAt: string | null;
  deletedAt: string | null;
  errorMessage: string | null;
}

export interface InvoiceAdminListResponse {
  id: string;
  invoiceNumber: string;
  subscriptionId: string;
  userId: string | null;
  userEmail: string | null;
  total: number;
  paidAmount: number;
  remainingAmount: number;
  status: string;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  dueDate: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface AdminSubscriptionsParams {
  status?: string;
  planCode?: string;
  page?: number;
  size?: number;
}

export interface AdminWidgetsParams {
  userId?: string;
  status?: string;
  page?: number;
  size?: number;
}

export interface AdminApiKeysParams {
  userId?: string;
  status?: string;
  page?: number;
  size?: number;
}

export interface AdminDataFilesParams {
  userId?: string;
  status?: string;
  fileType?: string;
  page?: number;
  size?: number;
}

export interface AdminInvoicesParams {
  userId?: string;
  status?: string;
  page?: number;
  size?: number;
}

const buildQueryString = (params: object) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

// Get subscriptions list
export const getAdminSubscriptions = (params: AdminSubscriptionsParams = {}) =>
  get<PagedResponse<SubscriptionAdminListResponse>>(`/admin/subscriptions${buildQueryString(params)}`);

// Get widgets list
export const getAdminWidgets = (params: AdminWidgetsParams = {}) =>
  get<PagedResponse<WidgetAdminListResponse>>(`/admin/widgets${buildQueryString(params)}`);

// Get API keys list
export const getAdminApiKeys = (params: AdminApiKeysParams = {}) =>
  get<PagedResponse<ApiKeyAdminListResponse>>(`/admin/api-keys${buildQueryString(params)}`);

// Get data files list
export const getAdminDataFiles = (params: AdminDataFilesParams = {}) =>
  get<PagedResponse<DataFileAdminListResponse>>(`/admin/data-files${buildQueryString(params)}`);

// Get invoices list
export const getAdminInvoices = (params: AdminInvoicesParams = {}) =>
  get<PagedResponse<InvoiceAdminListResponse>>(`/admin/invoices${buildQueryString(params)}`);
